import { adminClient, inferAdditionalFields, jwtClient, organizationClient } from 'better-auth/client/plugins';
import { createAuthClient } from 'better-auth/react';
import { setAccessTokenFetcher } from './tokenStore';

/**
 * The one place better-auth is imported. Everything else in the portal goes through the
 * exports below, so a version bump has a single blast radius — and so does swapping the
 * identity provider entirely, which is the point of running auth as its own service.
 */
export const authClient = createAuthClient({
  baseURL: import.meta.env.VITE_AUTH_URL,
  basePath: '/api/auth',
  fetchOptions: { credentials: 'include' },
  plugins: [
    // Mirrors the `locale` additionalField declared on apps/auth's user, so sign-up
    // accepts it in its input type and the session carries it back. The auth service
    // is a separate process, so its `typeof auth` can't be imported here; the manual
    // shape must be a `const` value so the literals survive inference into S.
    inferAdditionalFields({
      user: { locale: { type: 'string', required: false, input: true } },
    } as const),
    organizationClient(),
    adminClient(),
    jwtClient(),
  ],
});

setAccessTokenFetcher(async () => {
  const { data, error } = await authClient.token();
  if (error || !data?.token) {
    throw new Error(error?.message ?? 'Failed to mint access token');
  }
  return data.token;
});

export type AuthSession = ReturnType<typeof authClient.useSession>;
export type Organization = { id: string; name: string; slug: string; logo?: string | null };
export type Membership = { id: string; organizationId: string; role: 'owner' | 'admin' | 'member' };
